import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { 
  getOverdueInvoices, 
  sendReminder, 
  getReminderSettings, 
  updateReminderSettings,
  getInvoiceReminders 
} from '../lib/api';
import { 
  Bell, 
  Send, 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  Mail,
  Settings,
  Loader2,
  Calendar,
  User,
  FileText,
  History
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const RemindersPage = () => {
  const [overdueInvoices, setOverdueInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [reminderHistory, setReminderHistory] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);
  const navigate = useNavigate();
  
  const [settings, setSettings] = useState({
    enabled: false,
    reminder_days: [7, 14, 30],
    email_subject_template: "Rappel: Facture {invoice_number} en attente de paiement",
    email_body_template: `Bonjour {client_name},

Nous vous rappelons que la facture {invoice_number} d'un montant de {amount} {currency} est arrivée à échéance le {due_date}.

Nous vous prions de bien vouloir procéder au règlement dans les meilleurs délais.

Cordialement,
{company_name}`
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [invoicesRes, settingsRes] = await Promise.all([
        getOverdueInvoices(),
        getReminderSettings()
      ]);
      setOverdueInvoices(invoicesRes.data);
      if (settingsRes.data) {
        setSettings(prev => ({ ...prev, ...settingsRes.data }));
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminder = async (invoiceId) => {
    setSending(prev => ({ ...prev, [invoiceId]: true }));
    try {
      await sendReminder(invoiceId);
      toast.success('Rappel envoyé avec succès');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(prev => ({ ...prev, [invoiceId]: false }));
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await updateReminderSettings(settings);
      toast.success('Paramètres sauvegardés');
      setSettingsOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingSettings(false);
    }
  };

  const showHistory = async (invoice) => {
    setSelectedInvoice(invoice);
    setHistoryOpen(true);
    try {
      const res = await getInvoiceReminders(invoice.id);
      setReminderHistory(res.data);
    } catch (error) {
      setReminderHistory([]);
    }
  };

  const formatCurrency = (amount, currency = 'CHF') => {
    return new Intl.NumberFormat('fr-CH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount) + ' ' + currency;
  };

  const getUrgencyColor = (days) => {
    if (days > 30) return 'bg-red-500';
    if (days > 14) return 'bg-orange-500';
    return 'bg-yellow-500';
  };

  const getUrgencyLabel = (days) => {
    if (days > 30) return 'Critique';
    if (days > 14) return 'Urgent';
    return 'En retard';
  };

  const totalOverdue = overdueInvoices.reduce((sum, inv) => sum + inv.total, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reminders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900">Rappels de paiement</h1>
          <p className="text-slate-500 mt-1">Gérez les factures en retard et envoyez des rappels</p>
        </div>
        <Button variant="outline" onClick={() => setSettingsOpen(true)}>
          <Settings className="w-4 h-4 mr-2" />
          Paramètres
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-red-700">Factures en retard</p>
                <p className="text-2xl font-bold text-red-900">{overdueInvoices.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-orange-700">Montant total en retard</p>
                <p className="text-2xl font-bold text-orange-900">{formatCurrency(totalOverdue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Bell className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-blue-700">Rappels automatiques</p>
                <p className="text-2xl font-bold text-blue-900">{settings.enabled ? 'Activés' : 'Désactivés'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Overdue Invoices List */}
      {overdueInvoices.length === 0 ? (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-12 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-green-900 mb-2">Aucune facture en retard</h3>
            <p className="text-green-700">Toutes vos factures sont à jour. Excellent travail !</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <h2 className="font-semibold text-lg text-slate-900">Factures en attente de paiement</h2>
          {overdueInvoices.map(invoice => (
            <Card key={invoice.id} className="border-slate-200 hover:border-slate-300 transition-colors">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-2 h-full min-h-[60px] rounded-full ${getUrgencyColor(invoice.days_overdue)}`} />
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <FileText className="w-4 h-4 text-slate-400" />
                        <span className="font-semibold text-slate-900">{invoice.invoice_number}</span>
                        <Badge variant="outline" className={`text-xs ${getUrgencyColor(invoice.days_overdue)} text-white border-0`}>
                          {getUrgencyLabel(invoice.days_overdue)}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {invoice.client_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {invoice.client_email || 'Pas d\'email'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Échéance: {new Date(invoice.due_date).toLocaleDateString('fr-CH')}
                        </span>
                      </div>
                      <p className="text-sm text-red-600 mt-1 font-medium">
                        {invoice.days_overdue} jour{invoice.days_overdue > 1 ? 's' : ''} de retard
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xl font-bold text-slate-900">{formatCurrency(invoice.total)}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => showHistory(invoice)}
                      >
                        <History className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm"
                        onClick={() => handleSendReminder(invoice.id)}
                        disabled={sending[invoice.id] || !invoice.client_email}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {sending[invoice.id] ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Rappeler
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Paramètres des rappels</DialogTitle>
            <DialogDescription>
              Configurez les rappels automatiques et personnalisez vos messages
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">Rappels automatiques</p>
                <p className="text-sm text-slate-500">Envoyer automatiquement des rappels aux dates configurées</p>
              </div>
              <Switch 
                checked={settings.enabled} 
                onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Jours de rappel après échéance</Label>
              <Input 
                value={settings.reminder_days.join(', ')}
                onChange={(e) => {
                  const days = e.target.value.split(',').map(d => parseInt(d.trim())).filter(d => !isNaN(d));
                  setSettings({ ...settings, reminder_days: days });
                }}
                placeholder="7, 14, 30"
              />
              <p className="text-xs text-slate-500">Séparez les jours par des virgules (ex: 7, 14, 30)</p>
            </div>

            <div className="space-y-2">
              <Label>Sujet de l'email</Label>
              <Input 
                value={settings.email_subject_template}
                onChange={(e) => setSettings({ ...settings, email_subject_template: e.target.value })}
              />
              <p className="text-xs text-slate-500">
                Variables: {'{invoice_number}'}, {'{client_name}'}, {'{amount}'}, {'{currency}'}, {'{due_date}'}, {'{company_name}'}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Corps de l'email</Label>
              <Textarea 
                value={settings.email_body_template}
                onChange={(e) => setSettings({ ...settings, email_body_template: e.target.value })}
                rows={8}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Annuler</Button>
            <Button onClick={handleSaveSettings} disabled={savingSettings}>
              {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Historique des rappels</DialogTitle>
            <DialogDescription>
              {selectedInvoice?.invoice_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {reminderHistory.length === 0 ? (
              <p className="text-center text-slate-500 py-8">Aucun rappel envoyé pour cette facture</p>
            ) : (
              reminderHistory.map((reminder) => (
                <div key={reminder.id || reminder.sent_at} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Rappel envoyé</p>
                    <p className="text-sm text-slate-500">
                      {new Date(reminder.sent_at).toLocaleString('fr-CH')} - {reminder.client_email}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RemindersPage;
